import { LitheElement, LitheFC } from '@wheelbox/lithejs-engine';
import { goto } from '@wheelbox/lithejs-router';
import withTranslations from '@wheelbox/lithejs-i18n';
import { GitlabIcon, TwitterIcon, HeartIcon } from '@wheelbox/lithejs-lucide';

import { Separator } from '@components/ui';

export interface AppFooterProps {
  id?: string;
  testId?: string;
}

interface FooterLink {
  label: string;
  path?: string;
  href?: string;
  external?: boolean;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const AppFooter: LitheFC<AppFooterProps> = ({
  id = 'app-footer',
  testId = 'app-footer',
}): LitheElement<'footer'> => {
  const t = withTranslations('Common');

  const currentYear = new Date().getFullYear();

  const footerSections: FooterSection[] = [
    {
      title: t('footer.resources'),
      links: [
        { label: t('footer.links.docs'), path: '/docs' },
        { label: t('footer.links.tutorial'), path: '/tutorial' },
        { label: t('footer.links.examples'), path: '/examples' },
        { label: t('footer.links.blog'), path: '/blog' },
      ],
    },
    {
      title: t('footer.packages'),
      links: [
        {
          label: 'lithejs-engine',
          href: 'https://www.npmjs.com/package/@wheelbox/lithejs-engine',
          external: true,
        },
        {
          label: 'lithejs-router',
          href: 'https://www.npmjs.com/package/@wheelbox/lithejs-router',
          external: true,
        },
        {
          label: 'lithejs-state',
          href: 'https://www.npmjs.com/package/@wheelbox/lithejs-state',
          external: true,
        },
        {
          label: 'lithejs-ui',
          href: 'https://www.npmjs.com/package/@wheelbox/lithejs-ui',
          external: true,
        },
      ],
    },
    {
      title: t('footer.community'),
      links: [
        {
          label: 'GitLab',
          href: 'https://gitlab.com/lithejs',
          external: true,
        },
        {
          label: 'npm',
          href: 'https://www.npmjs.com/org/wheelbox',
          external: true,
        },
      ],
    },
  ];

  const renderLink = (link: FooterLink): LitheElement<'a'> => {
    if (link.external) {
      return [
        'a',
        {
          href: link.href,
          target: '_blank',
          rel: 'noopener noreferrer',
          class:
            'text-muted-foreground hover:text-foreground transition-colors text-sm',
        },
        link.label,
      ];
    }
    return [
      'a',
      {
        href: link.path,
        onclick: (e: MouseEvent) => {
          e.preventDefault();
          goto(link.path!);
        },
        class:
          'text-muted-foreground hover:text-foreground transition-colors text-sm cursor-pointer',
      },
      link.label,
    ];
  };

  return [
    'footer',
    {
      id,
      'data-testid': testId,
      class: 'bg-muted/30 border-t border-border mt-auto',
    },
    [
      'div',
      { class: 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12' },
      // Footer sections grid
      [
        'div',
        {
          class: 'grid grid-cols-2 md:grid-cols-4 gap-8 mb-8',
        },
        // Logo and description column
        [
          'div',
          { class: 'col-span-2 md:col-span-1' },
          [
            'a',
            {
              href: '/',
              onclick: (e: MouseEvent) => {
                e.preventDefault();
                goto('/');
              },
              class: 'flex items-center gap-2 mb-4 cursor-pointer',
            },
            [
              'img',
              {
                src: '/assets/lithe-symbol.svg',
                alt: 'LitheJS',
                class: 'h-8 w-8',
              },
            ],
            ['span', { class: 'font-bold text-lg text-foreground' }, 'LitheJS'],
          ],
          [
            'p',
            { class: 'text-sm text-muted-foreground leading-relaxed' },
            t('footer.description'),
          ],
        ],
        // Dynamic sections
        ...footerSections.map(
          (section): LitheElement<'div'> => [
            'div',
            {},
            [
              'h3',
              { class: 'font-semibold text-foreground mb-4 text-sm' },
              section.title,
            ],
            [
              'ul',
              { class: 'space-y-2' },
              ...section.links.map(
                (link): LitheElement<'li'> => ['li', {}, renderLink(link)]
              ),
            ],
          ]
        ),
      ],
      Separator({ classes: 'my-8' }),
      // Bottom bar
      [
        'div',
        {
          class: 'flex flex-col sm:flex-row items-center justify-between gap-4',
        },
        [
          'p',
          { class: 'text-sm text-muted-foreground flex items-center gap-1' },
          `© ${currentYear} LitheJS. `,
          t('footer.madeWith'),
          ' ',
          HeartIcon({ size: 14, classes: 'text-red-500 inline' }),
        ],
        // Social links
        [
          'div',
          { class: 'flex items-center gap-4' },
          [
            'a',
            {
              href: 'https://gitlab.com/lithejs',
              target: '_blank',
              rel: 'noopener noreferrer',
              class:
                'text-muted-foreground hover:text-foreground transition-colors',
              'aria-label': 'GitLab',
            },
            GitlabIcon({ size: 20 }),
          ],
          [
            'a',
            {
              href: 'https://twitter.com/lithejs',
              target: '_blank',
              rel: 'noopener noreferrer',
              class:
                'text-muted-foreground hover:text-foreground transition-colors',
              'aria-label': 'Twitter',
            },
            TwitterIcon({ size: 20 }),
          ],
        ],
      ],
    ],
  ];
};

export default AppFooter;
